import { useState, useCallback, useRef, useMemo } from 'react';
import type { DistributiveOmit } from './useMultiplayer';
import { MAP_BASE_PROMPT } from '@roll1/shared';
import type { ClientMessage } from '@roll1/shared';
import { useI18n } from '../i18n';

// ── Types ────────────────────────────────────────────────────────

export type OutpaintDirection = 'top' | 'right' | 'bottom' | 'left';

/** Клиентское состояние outpainting mode */
export interface OutpaintingState {
  direction: OutpaintDirection | null;
  expandPx: number;
  overlapPx: number;
  prompt: string;
  previewUrl: string | null;
  generating: boolean;
  error: string | null;
  newWidth: number | null;
  newHeight: number | null;
}

const INITIAL_STATE: OutpaintingState = {
  direction: null,
  expandPx: 512,
  overlapPx: 96,
  prompt: MAP_BASE_PROMPT,
  previewUrl: null,
  generating: false,
  error: null,
  newWidth: null,
  newHeight: null,
};

// ── Hook ─────────────────────────────────────────────────────────

interface UseOutpaintingOptions {
  sessionId: string;
  participantId: string;
  pageId: string;
  mapWidth: number;
  mapHeight: number;
  send: (msg: DistributiveOmit<ClientMessage, 'id' | 'timestamp'>) => void;
  showToast?: (message: string) => void;
}

export interface UseOutpaintingReturn extends OutpaintingState {
  setDirection: (dir: OutpaintDirection | null) => void;
  setExpandPx: (px: number) => void;
  setOverlapPx: (px: number) => void;
  setPrompt: (prompt: string) => void;
  /** Модель картинок (пакет 3 переезда AI); undefined — умолчание кампании. */
  model: string | undefined;
  setModel: (id: string | undefined) => void;
  /** Id сессии — для списка моделей в панели. */
  sessionId: string;
  generate: () => Promise<void>;
  accept: () => void;
  regenerate: () => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

export function useOutpainting(options: UseOutpaintingOptions): UseOutpaintingReturn {
  const { sessionId, participantId, pageId, mapWidth, mapHeight, send, showToast } = options;
  const [state, setState] = useState<OutpaintingState>(INITIAL_STATE);
  // Выбор модели живёт отдельно от состояния: сброс окна его не теряет.
  const [model, setModel] = useState<string | undefined>(undefined);
  const modelRef = useRef<string | undefined>(undefined);
  modelRef.current = model;
  const { t } = useI18n();
  const abortRef = useRef<AbortController | null>(null);

  const setDirection = useCallback((dir: OutpaintDirection | null) => {
    setState(s => ({ ...s, direction: dir, previewUrl: null, error: null }));
  }, []);

  const setExpandPx = useCallback((px: number) => {
    setState(s => ({ ...s, expandPx: Math.max(128, Math.min(1024, px)), previewUrl: null, error: null }));
  }, []);

  const setOverlapPx = useCallback((px: number) => {
    setState(s => ({ ...s, overlapPx: Math.max(32, Math.min(128, px)), previewUrl: null, error: null }));
  }, []);

  const setPrompt = useCallback((prompt: string) => {
    setState(s => ({ ...s, prompt }));
  }, []);

  const doGenerate = useCallback(async () => {
    const currentState = state;
    if (!currentState.direction || !currentState.prompt.trim()) return;

    // Validate ranges
    if (currentState.expandPx < 128 || currentState.expandPx > 1024) return;
    if (currentState.overlapPx < 32 || currentState.overlapPx > 128) return;

    // Warn if result exceeds 4096px
    const isHorizontal = currentState.direction === 'left' || currentState.direction === 'right';
    const resultW = isHorizontal ? mapWidth + currentState.expandPx : mapWidth;
    const resultH = isHorizontal ? mapHeight : mapHeight + currentState.expandPx;
    if (resultW > 4096 || resultH > 4096) {
      showToast?.(t('outpaint.sizeWarning'));
    }

    setState(s => ({ ...s, generating: true, error: null }));

    try {
      const formData = new FormData();
      formData.append('mode', 'outpaint');
      formData.append('direction', currentState.direction);
      formData.append('expandPx', String(currentState.expandPx));
      formData.append('overlapPx', String(currentState.overlapPx));
      formData.append('prompt', currentState.prompt);
      if (modelRef.current) formData.append('model', modelRef.current);
      formData.append('pageId', pageId);
      formData.append('width', String(mapWidth));
      formData.append('height', String(mapHeight));

      abortRef.current = new AbortController();

      const res = await fetch(
        `/api/generate-map?sessionId=${sessionId}&participantId=${participantId}`,
        { method: 'POST', body: formData, signal: abortRef.current.signal },
      );

      const data = await res.json() as { ok: boolean; url?: string; newWidth?: number; newHeight?: number; error?: string };
      if (data.ok && data.url) {
        setState(s => ({
          ...s,
          previewUrl: data.url!,
          newWidth: data.newWidth ?? null,
          newHeight: data.newHeight ?? null,
          generating: false,
        }));
      } else {
        setState(s => ({ ...s, generating: false, error: data.error ?? t('outpaint.errorGeneration') }));
        showToast?.(data.error ?? t('outpaint.errorGeneration'));
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setState(s => ({ ...s, generating: false }));
        return;
      }
      setState(s => ({ ...s, generating: false, error: t('outpaint.errorNetwork') }));
      showToast?.(t('outpaint.errorNetwork'));
    }
  }, [state, mapWidth, mapHeight, pageId, sessionId, participantId, showToast, t]);

  const accept = useCallback(() => {
    if (!state.previewUrl || !state.direction) return;
    send({
      type: 'map:outpaint-accept',
      url: state.previewUrl,
      pageId,
      direction: state.direction,
      expandPx: state.expandPx,
    });
    setState(INITIAL_STATE);
  }, [state.previewUrl, state.direction, state.expandPx, pageId, send]);

  const regenerate = useCallback(async () => {
    await doGenerate();
  }, [doGenerate]);

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setState(INITIAL_STATE);
  }, []);

  const reset = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setState(INITIAL_STATE);
  }, []);

  return useMemo(() => ({
    ...state,
    model,
    setModel,
    sessionId,
    setDirection,
    setExpandPx,
    setOverlapPx,
    setPrompt,
    generate: doGenerate,
    accept,
    regenerate,
    cancel,
    reset,
  }), [
    state, setDirection, setExpandPx, setOverlapPx, setPrompt,
    doGenerate, accept, regenerate, cancel, reset,
  ]);
}
